import { AnalyticEdit } from './../model/analyticEdit';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AnalyticService } from 'src/app/core/service/analytic.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';
import { finalize } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-analytic-list',
  templateUrl: './analytic-list.component.html',
  styleUrls: ['./analytic-list.component.scss']
})
export class AnalyticListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: AnalyticEdit;
  public selectedAnalytic: AnalyticEdit;
  public filterText = '';

  constructor(private analyticService: AnalyticService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private messengerService: MessengerService,
    private readonly destroyRef: DestroyRef)
  {
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const analyticId = this.selectedAnalytic.id;
      this.analyticService.deleteAnalytic(analyticId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== analyticId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('ANALYTIC-DELETED-SUCCESSFULLY');
          this.messengerService.sendMessage(new MessageSubject('reloadAnalytics'));
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedAnalytic = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {

    this.analyticService.getAnalyticById(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.selectedAnalytic = data as AnalyticEdit;
        this.open('deleteDialog');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new AnalyticEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.analyticService.getAnalyticById(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(analytic: AnalyticEdit): void
  {
    this.analyticService.saveAnalytic(analytic, this.isNew, analytic.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('ANALYTIC-SAVED-SUCCESSFULLY');
        this.messengerService.sendMessage(new MessageSubject('reloadAnalytics'));
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.analyticService.getAnalyticList().pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.view.stopLoading())).subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedOn = new Date(d.lastModifiedOn); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
